import { useState, useMemo } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Loader2, Plus, AlertCircle, Trash2, Pencil, PauseCircle, PlayCircle, ShieldOff } from 'lucide-react'
import { CreateUserSheet } from './CreateUserSheet'
import { CreateOrgWizard } from './CreateOrgWizard'
import { OrgProfileView } from './OrgProfileView'
import { useToast } from '@/hooks/use-toast'

const ONBOARDING_STEPS = [
  'cco_assigned','brand_set','charter_uploaded',
  'first_member','first_journalist','first_event','media_kit_uploaded',
]

const TYPE_LABELS: Record<string, { label: string; className: string }> = {
  standard:    { label: 'Standard',    className: 'bg-blue-100 text-blue-700' },
  association: { label: 'Association', className: 'bg-purple-100 text-purple-700' },
  journalist:  { label: 'Journalist',  className: 'bg-amber-100 text-amber-700' },
  individual:  { label: 'Individual',  className: 'bg-teal-100 text-teal-700' },
}

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  active:     { label: 'Active',      className: 'bg-green-100 text-green-700' },
  suspended:  { label: 'Suspended',   className: 'bg-amber-100 text-amber-700' },
  restricted: { label: 'Restricted',  className: 'bg-red-100 text-red-700' },
}

const KYC_LABELS: Record<string, { label: string; className: string }> = {
  not_started:    { label: 'Not Started',  className: 'bg-red-100 text-red-700' },
  submitted:      { label: 'Submitted',    className: 'bg-blue-100 text-blue-700' },
  docs_requested: { label: 'Docs Needed',  className: 'bg-amber-100 text-amber-700' },
  under_review:   { label: 'Under Review', className: 'bg-purple-100 text-purple-700' },
  approved:       { label: 'Verified',     className: 'bg-green-100 text-green-700' },
  rejected:       { label: 'Rejected',     className: 'bg-red-100 text-red-700' },
}

export function InstitutionsView() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [selectedOrgId,      setSelectedOrgId]      = useState<string | null>(null)
  const [createOrgOpen,      setCreateOrgOpen]      = useState(false)
  const [addUserOpen,        setAddUserOpen]        = useState(false)
  const [addUserOrgId,       setAddUserOrgId]       = useState('')
  const [addUserOrgName,     setAddUserOrgName]     = useState('')
  const [addUserDefaultRole, setAddUserDefaultRole] = useState<string | undefined>()

  // Multi-select
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())

  // Edit org state
  const [editOrg, setEditOrg]     = useState<any | null>(null)
  const [editForm, setEditForm]   = useState({ name: '', slug: '', contact_email: '', plan: '', status: '', org_type: '' })
  const [editSaving, setEditSaving] = useState(false)

  // Delete confirmation state
  const [deleteOrg, setDeleteOrg] = useState<{ id: string; name: string } | null>(null)
  const [deleteConfirmText, setDeleteConfirmText] = useState('')
  const [deleting, setDeleting] = useState(false)

  // Filters
  const [filterType,   setFilterType]   = useState('all')
  const [filterStatus, setFilterStatus] = useState('all')
  const [searchQuery,  setSearchQuery]  = useState('')

  const { data: institutionOrgs, isLoading, error } = useQuery({
    queryKey: ['admin-institution-orgs'],
    staleTime: 0,
    queryFn: async () => {
      const { data: orgs, error: orgError } = await supabase
        .from('organizations' as any)
        .select(`
          id, name, slug, locale_default, comms_email, created_at, settings, org_type, status,
          organization_members(
            id, user_id, status,
            roles(name)
          ),
          org_onboarding(
            cco_assigned, brand_set, charter_uploaded, first_member,
            first_journalist, first_event, media_kit_uploaded
          ),
          org_kyc(kyc_status)
        `)
        .not('settings->>is_platform_owner', 'eq', 'true')
        .order('created_at', { ascending: false }) as any

      if (orgError) throw orgError
      if (!orgs?.length) return []

      const userIds = [
        ...new Set(
          orgs.flatMap((o: any) =>
            (o.organization_members || []).map((m: any) => m.user_id)
          ).filter(Boolean)
        )
      ]

      let profileMap: Record<string, { name: string; avatar?: string }> = {}
      if (userIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, first_name, last_name, full_name, avatar_url')
          .in('user_id', userIds as string[])
        profileMap = Object.fromEntries(
          (profiles || []).map((p: any) => {
            const composed = [p.first_name, p.last_name].filter(Boolean).join(' ').trim()
            return [p.user_id, { name: composed || p.full_name || '', avatar: p.avatar_url }]
          })
        )
      }

      return orgs.map((org: any) => ({
        ...org,
        organization_members: (org.organization_members || []).map((m: any) => ({
          ...m,
          profiles: { id: m.user_id, full_name: profileMap[m.user_id]?.name || null, avatar_url: profileMap[m.user_id]?.avatar || null },
        })),
      }))
    },
  })

  // ── Filtered list ────────────────────────────────────────────────────────
  const filteredOrgs = useMemo(() => {
    return (institutionOrgs || []).filter((org: any) => {
      if (filterType !== 'all' && (org.org_type ?? 'standard') !== filterType) return false
      if (filterStatus !== 'all' && (org.status ?? 'active') !== filterStatus) return false
      if (searchQuery) {
        const q = searchQuery.toLowerCase()
        if (!org.name.toLowerCase().includes(q) && !org.slug.toLowerCase().includes(q)) return false
      }
      return true
    })
  }, [institutionOrgs, filterType, filterStatus, searchQuery])

  // ── Select all (filtered) ────────────────────────────────────────────────
  const allFilteredIds = filteredOrgs.map((o: any) => o.id)
  const allSelected = allFilteredIds.length > 0 && allFilteredIds.every((id: string) => selectedIds.has(id))

  function toggleSelectAll() {
    if (allSelected) {
      setSelectedIds(prev => {
        const next = new Set(prev)
        allFilteredIds.forEach((id: string) => next.delete(id))
        return next
      })
    } else {
      setSelectedIds(prev => new Set([...prev, ...allFilteredIds]))
    }
  }

  function toggleSelect(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }

  // ── Per-org actions ───────────────────────────────────────────────────────
  async function setOrgStatus(orgId: string, status: string) {
    const { error } = await (supabase
      .from('organizations' as any)
      .update({ status })
      .eq('id', orgId) as any)
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' })
    else {
      queryClient.invalidateQueries({ queryKey: ['admin-institution-orgs'] })
      queryClient.invalidateQueries({ queryKey: ['associations'] })
      queryClient.invalidateQueries({ queryKey: ['all-users'] })
      toast({ title: `Institution ${status}` })
    }
  }

  function openEdit(org: any) {
    setEditForm({
      name:          org.name ?? '',
      slug:          org.slug ?? '',
      contact_email: org.comms_email ?? '',
      plan:          org.plan ?? '',
      status:        org.status ?? 'active',
      org_type:      org.org_type ?? 'standard',
    })
    setEditOrg(org)
  }

  async function saveEdit() {
    if (!editOrg) return
    setEditSaving(true)
    try {
      const { error } = await (supabase.rpc('admin_update_organization' as any, {
        p_org_id: editOrg.id,
        p_patch: {
          name:          editForm.name,
          slug:          editForm.slug,
          contact_email: editForm.contact_email,
          plan:          editForm.plan,
          status:        editForm.status,
          org_type:      editForm.org_type,
        },
      }) as any)
      if (error) throw error
      toast({ title: 'Institution updated.' })
      queryClient.invalidateQueries({ queryKey: ['admin-institution-orgs'] })
      setEditOrg(null)
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' })
    } finally {
      setEditSaving(false)
    }
  }

  async function confirmDelete() {
    if (!deleteOrg || deleteConfirmText !== deleteOrg.name) return
    setDeleting(true)
    try {
      const { data, error } = await (supabase.rpc('admin_delete_organization' as any, {
        p_org_id: deleteOrg.id,
      }) as any)
      if (error) throw error
      const result = data as any
      toast({ title: 'Institution deleted', description: `"${result?.name}" and ${result?.members_removed} member(s) removed.` })
      queryClient.invalidateQueries({ queryKey: ['admin-institution-orgs'] })
      setDeleteOrg(null)
      setDeleteConfirmText('')
    } catch (e: any) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' })
    } finally {
      setDeleting(false)
    }
  }

  // ── Bulk actions ──────────────────────────────────────────────────────────
  async function bulkSetStatus(status: string) {
    const ids = [...selectedIds]
    const { error } = await (supabase
      .from('organizations' as any)
      .update({ status })
      .in('id', ids) as any)
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' })
    else {
      setSelectedIds(new Set())
      queryClient.invalidateQueries({ queryKey: ['admin-institution-orgs'] })
      toast({ title: `${ids.length} institution(s) ${status}` })
    }
  }

  async function bulkDelete() {
    const ids = [...selectedIds]
    if (!window.confirm(`Permanently delete ${ids.length} institution(s)? This cannot be undone.`)) return
    const { error } = await (supabase
      .from('organizations' as any)
      .delete()
      .in('id', ids) as any)
    if (error) toast({ title: 'Error', description: error.message, variant: 'destructive' })
    else {
      setSelectedIds(new Set())
      queryClient.invalidateQueries({ queryKey: ['admin-institution-orgs'] })
      toast({ title: `${ids.length} institution(s) deleted` })
    }
  }

  // ── Full-page profile view ────────────────────────────────────────────────
  if (selectedOrgId) {
    return (
      <OrgProfileView
        orgId={selectedOrgId}
        onBack={() => setSelectedOrgId(null)}
      />
    )
  }

  if (error) return (
    <div className="p-6 text-[13px] text-red-600 bg-red-50 border border-red-200 rounded-lg">
      Failed to load institutions. Please refresh the page.
      <br />
      <span className="text-[11px] text-muted-foreground font-mono">{(error as any).message}</span>
    </div>
  )

  return (
    <div className="space-y-4">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-[#0D2240]">Client Organisations</h2>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl text-justify">
            All client institutions using the CONORBIT platform. Each institution manages its own
            team, journalists, and events independently.
          </p>
        </div>
        <Button
          style={{ backgroundColor: '#006B3C' }}
          className="text-white hover:opacity-90 shrink-0"
          onClick={() => setCreateOrgOpen(true)}
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Institution
        </Button>
      </div>

      {/* ── Filter bar ── */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="text"
          placeholder="Search by name or slug…"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="px-3 py-1.5 border border-[#DDD8CE] rounded-lg text-[12.5px] bg-background focus:outline-none focus:border-[#006B3C] w-52"
        />
        <div className="flex items-center gap-1 bg-[#F7F5F0] border border-[#DDD8CE] rounded-lg p-0.5">
          {['all','standard','association','journalist','individual'].map(v => (
            <button
              key={v}
              onClick={() => setFilterType(v)}
              className={`px-2.5 py-1 rounded-md text-[11.5px] font-medium capitalize transition-colors ${
                filterType === v ? 'bg-white text-[#0D2240] shadow-sm' : 'text-muted-foreground hover:text-[#0D2240]'
              }`}
            >
              {v === 'all' ? 'All Types' : v}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1 bg-[#F7F5F0] border border-[#DDD8CE] rounded-lg p-0.5">
          {['all','active','suspended','restricted'].map(v => (
            <button
              key={v}
              onClick={() => setFilterStatus(v)}
              className={`px-2.5 py-1 rounded-md text-[11.5px] font-medium capitalize transition-colors ${
                filterStatus === v ? 'bg-white text-[#0D2240] shadow-sm' : 'text-muted-foreground hover:text-[#0D2240]'
              }`}
            >
              {v === 'all' ? 'All Status' : v}
            </button>
          ))}
        </div>
        {(filterType !== 'all' || filterStatus !== 'all' || searchQuery) && (
          <button
            onClick={() => { setFilterType('all'); setFilterStatus('all'); setSearchQuery('') }}
            className="text-[11.5px] text-muted-foreground hover:text-[#0D2240] underline"
          >
            Clear filters
          </button>
        )}
        <span className="ml-auto text-[11.5px] text-muted-foreground">
          {filteredOrgs.length} of {(institutionOrgs || []).length} institutions
        </span>
      </div>

      {/* ── Bulk action toolbar ── */}
      {selectedIds.size > 0 && (
        <div className="flex items-center gap-2 px-3 py-2 bg-[#0D2240] text-[#F0E4B8] rounded-lg text-[12.5px]">
          <span className="font-medium">{selectedIds.size} selected</span>
          <div className="w-px h-4 bg-[#F0E4B8]/30" />
          <button
            onClick={() => bulkSetStatus('suspended')}
            className="flex items-center gap-1 px-2.5 py-1 bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 rounded text-[11.5px] font-medium transition-colors"
          >
            <PauseCircle size={12} /> Suspend
          </button>
          <button
            onClick={() => bulkSetStatus('active')}
            className="flex items-center gap-1 px-2.5 py-1 bg-green-500/20 hover:bg-green-500/30 text-green-200 rounded text-[11.5px] font-medium transition-colors"
          >
            <PlayCircle size={12} /> Activate
          </button>
          <button
            onClick={() => bulkSetStatus('restricted')}
            className="flex items-center gap-1 px-2.5 py-1 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded text-[11.5px] font-medium transition-colors"
          >
            <ShieldOff size={12} /> Restrict
          </button>
          <button
            onClick={bulkDelete}
            className="flex items-center gap-1 px-2.5 py-1 bg-red-700/30 hover:bg-red-700/40 text-red-300 rounded text-[11.5px] font-medium transition-colors"
          >
            <Trash2 size={12} /> Delete
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto text-[#F0E4B8]/60 hover:text-[#F0E4B8] text-lg leading-none"
            title="Clear selection"
          >×</button>
        </div>
      )}

      {/* ── Table ── */}
      <div className="rounded-lg border overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow style={{ backgroundColor: '#0D2240' }}>
              <TableHead className="w-8 pl-4">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  className="accent-[#F0E4B8] cursor-pointer"
                />
              </TableHead>
              {['Institution', 'Type', 'Institution Admin', 'Members', 'KYC', 'Status', 'Created'].map(h => (
                <TableHead key={h} style={{ color: '#F0E4B8' }} className="font-semibold">{h}</TableHead>
              ))}
              <TableHead style={{ color: '#F0E4B8' }} className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : filteredOrgs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-12 text-muted-foreground text-sm">
                  {(institutionOrgs || []).length === 0
                    ? 'No institutions yet. Create the first one.'
                    : 'No institutions match the current filters.'}
                </TableCell>
              </TableRow>
            ) : filteredOrgs.map((org: any, idx: number) => {
              const cco = (org.organization_members || []).find(
                (m: any) => m.roles?.name === 'CCO' && m.status === 'active'
              )
              const memberCount = (org.organization_members || []).filter(
                (m: any) => m.status === 'active'
              ).length

              const onboarding = Array.isArray(org.org_onboarding)
                ? org.org_onboarding[0]
                : org.org_onboarding
              const onboardingScore = onboarding
                ? ONBOARDING_STEPS.filter(s => onboarding[s]).length
                : 0

              const kycRaw    = org.org_kyc
              const kycStatus = (Array.isArray(kycRaw) ? kycRaw[0] : kycRaw)?.kyc_status ?? 'not_started'
              const kycUi     = KYC_LABELS[kycStatus] ?? KYC_LABELS.not_started

              const orgType   = org.org_type ?? 'standard'
              const orgStatus = org.status    ?? 'active'
              const typeUi    = TYPE_LABELS[orgType]   ?? TYPE_LABELS.standard
              const statusUi  = STATUS_LABELS[orgStatus] ?? STATUS_LABELS.active
              const isSelected = selectedIds.has(org.id)

              return (
                <TableRow
                  key={org.id}
                  className={`${idx % 2 === 1 ? 'bg-[#F7F5F0]' : ''} ${isSelected ? 'ring-1 ring-inset ring-[#006B3C]/30 bg-[#E8F5EE]/60' : ''}`}
                >
                  {/* Checkbox */}
                  <TableCell className="pl-4">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleSelect(org.id)}
                      className="accent-[#006B3C] cursor-pointer"
                    />
                  </TableCell>

                  {/* Institution name */}
                  <TableCell>
                    <button
                      onClick={() => setSelectedOrgId(org.id)}
                      className="text-left group"
                    >
                      <div className="font-medium text-[13px] text-[#0D2240] group-hover:underline">{org.name}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">{org.slug}</div>
                    </button>
                  </TableCell>

                  {/* Type */}
                  <TableCell>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${typeUi.className}`}>
                      {typeUi.label}
                    </span>
                  </TableCell>

                  {/* Institution Admin */}
                  <TableCell>
                    {cco ? (
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-[#0D2240]/10 flex items-center justify-center shrink-0 overflow-hidden text-[10px] font-bold text-[#0D2240]">
                          {cco.profiles?.avatar_url
                            ? <img src={cco.profiles.avatar_url} className="w-full h-full object-cover" alt="" />
                            : (cco.profiles?.full_name || '?').split(' ').map((n: string) => n[0]).join('').slice(0,2).toUpperCase()
                          }
                        </div>
                        <div>
                          <div className="text-[12.5px] font-medium leading-tight">
                            {cco.profiles?.full_name || 'Name pending'}
                          </div>
                          <div className="text-[11px] text-[#006B3C]">{org.org_type === 'association' ? 'President' : 'Institution Admin'}</div>
                        </div>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          setAddUserOrgId(org.id)
                          setAddUserOrgName(org.name)
                          setAddUserDefaultRole('CCO')
                          setAddUserOpen(true)
                        }}
                        className="flex items-center gap-1 text-[11px] font-medium text-amber-600 hover:text-amber-700"
                      >
                        <AlertCircle size={11} />
                        Assign Institution Admin
                      </button>
                    )}
                  </TableCell>

                  {/* Members */}
                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[13px] font-medium">{memberCount}</span>
                      <div className="h-1 w-10 bg-[#DDD8CE] rounded-full overflow-hidden">
                        <div
                          className="h-full bg-[#006B3C] rounded-full"
                          style={{ width: `${(onboardingScore / ONBOARDING_STEPS.length) * 100}%` }}
                        />
                      </div>
                      <span className="text-[10px] text-muted-foreground">{onboardingScore}/{ONBOARDING_STEPS.length}</span>
                    </div>
                  </TableCell>

                  {/* KYC */}
                  <TableCell>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${kycUi.className}`}>
                      {kycUi.label}
                    </span>
                  </TableCell>

                  {/* Status */}
                  <TableCell>
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide ${statusUi.className}`}>
                      {statusUi.label}
                    </span>
                  </TableCell>

                  {/* Created */}
                  <TableCell className="text-[12px] text-muted-foreground whitespace-nowrap">
                    {new Date(org.created_at).toLocaleDateString('en-GB', {
                      day: '2-digit', month: 'short', year: 'numeric',
                    })}
                  </TableCell>

                  {/* Actions */}
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1.5 flex-wrap">
                      <button
                        onClick={() => {
                          setAddUserOrgId(org.id)
                          setAddUserOrgName(org.name)
                          setAddUserDefaultRole(undefined)
                          setAddUserOpen(true)
                        }}
                        className="px-2.5 py-1 bg-[#006B3C] text-white rounded-lg text-[11px] font-medium hover:bg-[#005A32]"
                      >
                        Add User
                      </button>
                      {orgStatus === 'active' ? (
                        <button
                          onClick={() => setOrgStatus(org.id, 'suspended')}
                          title="Suspend institution"
                          className="px-2.5 py-1 border border-amber-300 text-amber-700 rounded-lg text-[11px] font-medium hover:bg-amber-50"
                        >
                          Suspend
                        </button>
                      ) : (
                        <button
                          onClick={() => setOrgStatus(org.id, 'active')}
                          title="Reactivate institution"
                          className="px-2.5 py-1 border border-green-300 text-green-700 rounded-lg text-[11px] font-medium hover:bg-green-50"
                        >
                          Activate
                        </button>
                      )}
                      {orgStatus !== 'restricted' && (
                        <button
                          onClick={() => setOrgStatus(org.id, 'restricted')}
                          title="Restrict institution"
                          className="px-2.5 py-1 border border-red-200 text-red-600 rounded-lg text-[11px] font-medium hover:bg-red-50"
                        >
                          Restrict
                        </button>
                      )}
                      <button
                        onClick={() => openEdit(org)}
                        className="p-1.5 border border-[#DDD8CE] text-muted-foreground rounded-lg hover:bg-[#F7F5F0]"
                        title="Edit institution"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={() => { setDeleteOrg({ id: org.id, name: org.name }); setDeleteConfirmText('') }}
                        className="p-1.5 border border-red-200 text-red-500 rounded-lg hover:bg-red-50"
                        title="Delete institution"
                      >
                        <Trash2 size={12} />
                      </button>
                      <button
                        onClick={() => setSelectedOrgId(org.id)}
                        className="px-2.5 py-1 border border-[#DDD8CE] rounded-lg text-[11px] text-muted-foreground hover:bg-[#F7F5F0]"
                      >
                        Profile
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </div>

      {/* Create Institution Wizard */}
      <CreateOrgWizard
        open={createOrgOpen}
        onClose={() => setCreateOrgOpen(false)}
        onSuccess={(orgId, orgName) => {
          setAddUserOrgId(orgId)
          setAddUserOrgName(orgName)
          setAddUserDefaultRole('CCO')
          setAddUserOpen(true)
        }}
      />

      {/* Add User Sheet */}
      <CreateUserSheet
        org_id={addUserOrgId}
        org_name={addUserOrgName}
        open={addUserOpen}
        onClose={() => setAddUserOpen(false)}
        defaultRole={addUserDefaultRole}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['admin-institution-orgs'] })
        }}
      />

      {/* ── Edit Institution Sheet ─────────────────────────────────────── */}
      <Sheet open={!!editOrg} onOpenChange={o => { if (!o) setEditOrg(null) }}>
        <SheetContent className="w-full max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Edit Institution</SheetTitle>
          </SheetHeader>
          <div className="space-y-4 mt-6">
            <div>
              <Label>Name</Label>
              <Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <Label>Slug</Label>
              <Input value={editForm.slug} onChange={e => setEditForm(f => ({ ...f, slug: e.target.value }))} />
            </div>
            <div>
              <Label>Contact Email</Label>
              <Input type="email" value={editForm.contact_email} onChange={e => setEditForm(f => ({ ...f, contact_email: e.target.value }))} />
            </div>
            <div>
              <Label>Organisation Type</Label>
              <Select value={editForm.org_type} onValueChange={v => setEditForm(f => ({ ...f, org_type: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="standard">Standard</SelectItem>
                  <SelectItem value="association">Association</SelectItem>
                  <SelectItem value="journalist">Journalist</SelectItem>
                  <SelectItem value="individual">Individual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Plan</Label>
              <Input value={editForm.plan} onChange={e => setEditForm(f => ({ ...f, plan: e.target.value }))} placeholder="basic / pro / enterprise" />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={v => setEditForm(f => ({ ...f, status: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="restricted">Restricted</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button className="w-full" onClick={saveEdit} disabled={editSaving}>
              {editSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Changes
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* ── Delete Confirmation Dialog ─────────────────────────────────── */}
      <AlertDialog open={!!deleteOrg} onOpenChange={o => { if (!o) { setDeleteOrg(null); setDeleteConfirmText('') } }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">Delete Institution</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <span>This will permanently delete <strong>"{deleteOrg?.name}"</strong> and all its data including members, events, press releases, and accreditations. This cannot be undone.</span>
              <span className="block mt-2">Type <strong>{deleteOrg?.name}</strong> to confirm:</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={deleteConfirmText}
            onChange={e => setDeleteConfirmText(e.target.value)}
            placeholder={deleteOrg?.name}
          />
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700"
              disabled={deleteConfirmText !== deleteOrg?.name || deleting}
              onClick={confirmDelete}
            >
              {deleting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
